<template>
  <div
    class="product-card"
    :class="{
      'is-selected': selected,
      'product-card--selected': selectedAsPackage,
    }"
  >
    <div
      class="position-relative"
      :class="{ 'product-card__clickable': isImageClickable }"
      @click="
        $emit('open-modal', {
          productData: product,
        })
      "
    >
      <div class="product-card__labels">
        <div
          v-if="dietaryLabels && dietaryLabels.length"
          class="product-card__attributes"
        >
          <div
            v-for="label in dietaryLabels"
            :key="label"
            class="badge badge--sm product-card__label"
            :aria-label="label"
          >
            {{ label }}
          </div>
        </div>
        <div
          v-if="showBrievenbusLabel"
          class="badge badge--sm product-card__label"
        >
          Brievenbus
        </div>
      </div>

      <figure
        class="rimg rimg--contain"
        :class="isImageHigher ? 'rimg--100-65' : 'rimg--100-56'"
      >
        <img
          v-if="product.image && product.image !== 'false'"
          :src="product?.images[0]?.medium || product.image"
          :alt="product.name"
          loading="lazy"
        />
      </figure>
    </div>
    <div class="product-card__content">
      <span class="product-card__title">{{ decodedName }}</span>
      <span class="product-card__price">
        <span>{{ formattedPrice }}</span> ex. BTW
      </span>

      <!-- <span
        v-if="formattedPriceInclVat && formattedPriceInclVat !== ''"
        class="product-card__price product-card__price--incl-vat"
      >
        <span>{{ formattedPriceInclVat }}</span> incl. BTW
      </span> -->

      <!-- Packaging -->
      <template v-if="isPackaging">
        <button
          @click.stop="$emit('select', product)"
          class="btn mt-auto"
          :class="{ 'btn--gray': !selected, 'btn--primary': selected }"
          aria-label="Kies verpakking"
        >
          {{ selected ? 'Gekozen' : 'Kies deze' }}
          <i class="icon icon-checkmark"></i>
        </button>
      </template>

      <!-- Package or personalised card -->
      <template v-else-if="isPackage || isPersonalizedCard || count === 0">
        <button
          v-if="selectedAsPackage"
          @click.stop="
            alert(
              'Je hebt dit pakket al gekozen als verpakking, verwijder eerst de verpakking en probeer het opnieuw.',
            )
          "
          class="btn mt-auto btn--secondary"
          aria-label="Gekozen als verpakking"
        >
          Verpakking <i class="icon icon-checkmark"></i>
        </button>
        <button
          v-else
          @click.stop="$emit('select', product)"
          class="btn mt-auto"
          :class="{ 'btn--gray': !selected, 'btn--primary': selected }"
          :aria-label="
            isPackage || isPersonalizedCard
              ? 'Kies dit pakket'
              : 'Voeg product toe'
          "
        >
          {{ getPackageButtonText() }}
          <i
            :class="`icon ${isPackage || isPersonalizedCard ? 'icon-checkmark' : 'icon-plus'}`"
          ></i>
        </button>
      </template>

      <!-- Other products with count -->
      <template v-else-if="count > 0 && !isPackage">
        <div class="product-card__actions">
          <button
            @click.stop="$emit('decrease', product)"
            class="btn btn--icon-lg btn--gray"
            aria-label="Verlaag aantal"
          >
            <i class="icon icon-min"></i>
          </button>
          <div class="quantity">{{ count }}</div>
          <button
            @click.stop="$emit('increase', product)"
            class="btn btn--icon-lg btn--primary"
            aria-label="Verhoog aantal"
          >
            <i class="icon icon-plus text-white"></i>
          </button>
          <button
            @click.stop="$emit('remove', product)"
            class="remove ms-2"
            aria-label="Verwijder product"
          >
            <i class="icon icon-trashbin ms-0"></i>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    product: { type: Object, required: true },
    count: { type: Number, default: 0 },
    isPackage: { type: Boolean, default: false },
    isPackaging: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    isPersonalizedCard: { type: Boolean, default: false },
    packageType: { type: String, default: 'large' },
    isImageClickable: { type: Boolean, default: false },
    isImageHigher: { type: Boolean, default: false },
    selectedAsPackage: { type: Boolean, default: false },
  },
  emits: ['select', 'increase', 'decrease', 'remove', 'open-modal'],
  computed: {
    formattedPrice() {
      let price = parseFloat(this.product.price);

      // For base packages, we need to adjust the price based on packaging selection
      if (this.isPackage) {
        // Check if there's user-selected packaging
        let packagingToUse = null;
        if (
          this.product.userPackagingMode === 'product' &&
          this.product.userPackagingProduct
        ) {
          packagingToUse = this.product.userPackagingProduct;
        } else if (
          this.product.userPackagingMode === 'box' &&
          this.product.userPackaging
        ) {
          packagingToUse = this.product.userPackaging;
        } else if (this.product.packaging) {
          // Fallback to package's default packaging
          packagingToUse = this.product.packaging;
        }

        // Check if the selected packaging is the same as the default packaging
        const isUsingDefaultPackaging = Boolean(
          this.product.packaging &&
            packagingToUse &&
            packagingToUse.id === this.product.packaging.id,
        );

        // Additional check: if no packaging is explicitly selected, don't add packaging costs
        const hasExplicitPackaging =
          (this.product.userPackagingMode === 'box' &&
            this.product.userPackaging) ||
          (this.product.userPackagingMode === 'product' &&
            this.product.userPackagingProduct);

        // If we have explicit packaging that's different from default, adjust the base price
        if (
          hasExplicitPackaging &&
          this.product.packaging &&
          !isUsingDefaultPackaging
        ) {
          // Subtract the default packaging price from the base package price
          const defaultPackagingPrice = parseFloat(
            this.product.packaging.price || 0,
          );
          price -= defaultPackagingPrice;
        }

        if (packagingToUse) {
          if (!isUsingDefaultPackaging && hasExplicitPackaging) {
            const packagingPrice = parseFloat(packagingToUse.price || 0);
            const basePackageProducts = this.product.products || [];
            const basePackageFoodDrinks = this.product.foodDrinks || [];

            // Check if the packaging is already in the package
            const isPackagingInPackage =
              basePackageProducts.some((p) => p.id === packagingToUse.id) ||
              basePackageFoodDrinks.some((f) => f.id === packagingToUse.id);

            // Only add packaging cost if it's not already in the package
            if (!isPackagingInPackage) {
              price += packagingPrice;
            }
          }
        }
      }

      return isNaN(price) ? '0,00' : price.toFixed(2).replace('.', ',');
    },
    formattedPriceInclVat() {
      const rawPrice = this.product.price_incl_tax;
      if (rawPrice === '' || rawPrice === null || rawPrice === undefined) {
        return '';
      }

      const price = parseFloat(rawPrice);
      if (isNaN(price)) {
        return '';
      }

      return price.toFixed(2).replace('.', ',');
    },
    decodedName() {
      const txt = document.createElement('textarea');
      txt.innerHTML = this.product.name ?? '';
      return txt.value;
    },
    showBrievenbusLabel() {
      if (this.packageType === 'small') return false;

      if (this.isPackage && this.product.size === 'Brievenbus') {
        return true;
      }

      const attrs = this.product.attributes || [];

      const brievenbusAttr = attrs.find((attr) => {
        const nameMatch =
          attr.name === 'pa_brievenbus-geschikt' ||
          attr.slug === 'pa_brievenbus-geschikt' ||
          attr.name?.toLowerCase().includes('brievenbus') ||
          attr.slug?.toLowerCase().includes('brievenbus') ||
          attr.name?.toLowerCase().includes('geschikt') ||
          attr.slug?.toLowerCase().includes('geschikt');
        return nameMatch;
      });

      if (!brievenbusAttr) return false;

      const valueMatch =
        brievenbusAttr.options?.includes?.('ja') ||
        brievenbusAttr.value === 'ja';

      return valueMatch;
    },
    dietaryLabels() {
      const attrs = this.product.attributes || [];
      const labels = [];

      const isYes = (attr) => {
        const value = attr?.value?.toLowerCase?.();
        return value === 'ja';
      };

      const getLabel = (attr) => {
        const key = (attr.slug || attr.name || '').toLowerCase();

        switch (true) {
          case key === 'pa_glutenvrij':
            return 'Glutenvrij';
          case key === 'pa_biologisch':
            return 'Biologisch';
          case key === 'pa_vegan':
            return 'Vegan';
          case key === 'pa_halal':
            return 'Halal';
          default:
            return null;
        }
      };

      attrs.forEach((attr) => {
        const label = getLabel(attr);
        if (label && isYes(attr)) {
          labels.push(label);
        }
      });

      return labels;
    },
  },
  methods: {
    alert(message) {
      alert(message);
    },
    getPackageButtonText() {
      if (this.isPackage && this.selected) {
        return 'Gekozen';
      }
      if (this.isPackage) {
        return 'Kies deze';
      }
      if (this.isPersonalizedCard && this.selected) {
        return 'Gekozen';
      }
      if (this.isPersonalizedCard) {
        return 'Kies kaart';
      }
      if (this.selected) {
        return 'Gekozen';
      }
      return 'Toevoegen';
    },
  },
};
</script>
